import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarRange, Sprout } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DEFAULT_MIN_POST_AGE_DAYS, MIN_INSIGHT_POSTS } from '@/lib/social-analytics/insightThresholds';
import type { InsightCardState } from '@/lib/social-analytics/resolveInsightCardState';

type InsightCardBodyProps = {
  state: InsightCardState;
  minPostAgeDays?: number | null;
  errorKey: string | null;
  onRetry: () => void;
  children: ReactNode;
};

/** The shared loading, error and empty states of the insight cards; renders `children` when ready. */
export const InsightCardBody = ({ state, minPostAgeDays, errorKey, onRetry, children }: InsightCardBodyProps) => {
  const { t } = useTranslation();
  const days = minPostAgeDays ?? DEFAULT_MIN_POST_AGE_DAYS;
  if (state === 'loading') return <Skeleton data-testid="insight-card-loading" className="h-40 w-full rounded-lg" />;
  if (state === 'error') {
    return (
      <div role="alert" className="flex flex-col items-center gap-3 py-8 text-center">
        <p className="text-sm text-muted-foreground">{t(errorKey ?? 'analytics.insights.error')}</p>
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>{t('analytics.error.retry')}</Button>
      </div>
    );
  }
  if (state === 'shortRange' || state === 'notEnough') {
    const Icon = state === 'shortRange' ? CalendarRange : Sprout;
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <Icon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm font-semibold">{t(`analytics.insights.${state}.title`)}</p>
        <p className="max-w-xs text-xs text-muted-foreground">{t(`analytics.insights.${state}.description`, { days, count: MIN_INSIGHT_POSTS })}</p>
      </div>
    );
  }
  return <>{children}</>;
};
